import { BuildingOfficeIcon, PencilIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Card, CardBody, CardHeader } from '@heroui/react';
import { type FC, useEffect, useState } from 'react';
import { DescriptionFormDialog } from '@/app/components/description-form-dialog.tsx';
import { NoData } from '@/app/components/no-data.tsx';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import toast from '@/app/toast.ts';
import type { AccountMeetingLocationModel, AccountModel } from '@/app/types/account.type.ts';
import {
  useDeleteAccountMeetingLocationMutation,
  usePostAccountMeetingLocationMutation,
  usePutAccountMeetingLocationMutation,
} from '@/slices/account-api-slice.ts';

export interface MeetingLocationsProps {
  account: AccountModel;
}

export const MeetingLocations: FC<MeetingLocationsProps> = ({ ...props }) => {
  const [
    postMeetingLocation,
    { isLoading: postLoading, isSuccess: postSuccess, isError: postError },
  ] = usePostAccountMeetingLocationMutation();
  const [putMeetingLocation, { isLoading: putLoading, isSuccess: putSuccess, isError: putError }] =
    usePutAccountMeetingLocationMutation();
  const [
    deleteMeetingLocation,
    { isLoading: deleteLoading, isSuccess: deleteSuccess, isError: deleteError },
  ] = useDeleteAccountMeetingLocationMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    let action = '';
    if (postSuccess || postError) {
      action = 'added';
    } else if (putSuccess || putError) {
      action = 'updated';
    } else if (deleteSuccess || deleteError) {
      action = 'deleted';
    }

    if (postSuccess || putSuccess || deleteSuccess) {
      toast.success(`Meeting Room successfully ${action}.`);
    }
    if (postError || putError || deleteError) {
      toast.error(`An error has occurred. Meeting room could not be ${action}.`);
    }
  }, [postSuccess, postError, putSuccess, putError, deleteSuccess, deleteError]);

  const handleDelete = (value: AccountMeetingLocationModel) => {
    deleteMeetingLocation({ accountKey: props.account.key, id: value.id });
  };

  const handleOnClose = (value: string) => {
    if (value && selectedValue?.id) {
      putMeetingLocation({
        id: selectedValue.id,
        description: value,
        accountId: selectedValue.accountId,
      });
    } else if (value && !selectedValue?.id) {
      postMeetingLocation({
        id: 0,
        description: value,
        accountId: props.account.id,
      });
    }

    setIsOpen(false);
  };

  const openEditDialog = (value: AccountMeetingLocationModel) => {
    setTitle('Edit Meeting Room');
    setSelectedValue(value);
    setIsOpen(true);
  };

  const openAddDialog = () => {
    setTitle('Add Meeting Room');
    setSelectedValue(null);
    setIsOpen(true);
  };

  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    putLoading || postLoading || deleteLoading
      ? showPageLoading('edit-meeting-locations')
      : hidePageLoading('edit-meeting-locations');
  }, [putLoading, postLoading, deleteLoading]);

  const meetingLocations = props.account?.meetingLocations ?? [];

  return (
    <>
      <DescriptionFormDialog
        placeHolder='Room # or name...'
        title={title}
        initialValue={selectedValue?.description ?? ''}
        label='Description'
        onClose={handleOnClose}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      <Card>
        <CardHeader className='flex justify-between items-center px-4 pt-4'>
          <div className='flex items-center gap-2'>
            <BuildingOfficeIcon className='size-5' aria-hidden='true' />
            <h2 className='font-semibold'>Meeting Rooms</h2>
            <div className='h-6 w-6 flex items-center justify-center font-semibold rounded-full bg-default-200 text-default-500 text-sm'>
              {meetingLocations.length}
            </div>
          </div>
          <Button color='primary' variant='bordered' onPress={openAddDialog}>
            <PlusIcon className='size-5' />
            Add Meeting Room
          </Button>
        </CardHeader>
        <CardBody className='px-4 pb-4'>
          <div className='flex-col'>
            {!meetingLocations.length && <NoData message='No meeting rooms found' />}
            {meetingLocations.map(location => {
              return (
                <div
                  className='flex items-center px-4 py-2 listGroupItem bg-default-50'
                  key={location.id}
                >
                  <div className='flex-1'>{location.description}</div>
                  <div className='flex gap-2'>
                    <Button
                      isIconOnly
                      size='sm'
                      variant='flat'
                      color='primary'
                      onPress={() => openEditDialog(location)}
                    >
                      <PencilIcon className='size-4' />
                    </Button>
                    <Button
                      isIconOnly
                      size='sm'
                      color='danger'
                      variant='flat'
                      onPress={() => handleDelete(location)}
                    >
                      <XMarkIcon className='size-4' />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
    </>
  );
};
